// src/components/CookiesPolicy.jsx
import React from "react";
import styled from "styled-components";
import { useLanguage } from "./LanguageContext";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

const Page = styled.div`
  max-width: 1000px;
  margin: 24px auto 48px;
  padding: 0 16px;
`;

const Title = styled.h1`
  font-size: clamp(22px, 3vw, 32px);
  line-height: 1.2;
  font-weight: 900;
  color: ${COLORS.text};
  margin: 6px 0 6px;
`;

const Sub = styled.p`
  color: ${COLORS.dim};
  margin: 4px 0 18px;
`;

const MetaBar = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  color: ${COLORS.dim};
  font-size: 13px;
  margin-bottom: 18px;
`;

const Card = styled.section`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: ${COLORS.shadow};
  padding: 18px;
  margin: 16px 0;
`;

const H2 = styled.h2`
  font-size: 18px;
  font-weight: 900;
  color: ${COLORS.text};
  margin: 0 0 10px;
`;

const P = styled.p`
  color: ${COLORS.text};
  line-height: 1.65;
  margin: 10px 0;
`;

const UL = styled.ul`
  padding-left: 18px;
  margin: 8px 0;
  li {
    margin: 6px 0;
    color: ${COLORS.text};
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: 8px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid ${COLORS.line};
    vertical-align: top;
  }

  th {
    white-space: nowrap;
    font-weight: 900;
    color: ${COLORS.text};
    background: ${COLORS.subtle};
  }

  td {
    color: ${COLORS.text};
  }

  code {
    font-size: 12px;
    background: ${COLORS.subtle};
    padding: 2px 6px;
    border-radius: 6px;
    border: 1px solid ${COLORS.line};
  }
`;

const Banner = styled.div`
  display: grid;
  gap: 10px;
  border: 1px dashed ${COLORS.line};
  background: #fffef8;
  border-radius: 14px;
  padding: 14px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  cursor: pointer;
  font-weight: 900;
  transition: transform .12s ease, filter .12s ease, background .12s ease;
  &:hover { transform: translateY(-1px); }
  &:active { transform: scale(.98); }
`;

const BtnPrimary = styled(Btn)`
  background: ${COLORS.text};
  color: #fff;
`;

const TOC = styled.nav`
  display: grid;
  gap: 8px;
  padding: 12px;
  background: ${COLORS.subtle};
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  a {
    color: ${COLORS.text};
    text-decoration: none;
    font-weight: 700;
  }
`;

const Small = styled.small`
  color: ${COLORS.dim};
`;

const LAST_UPDATED = "2025-10-04";
const CONTACT_EMAIL = "info@shopaholics.ge"; // შეცვალე, თუ სხვა მისამართია

function openCookieSettings() {
  // თუ გაქვს CMP/ბანერი, ამ event-ზე დააბაინდე გამხსნელი ლოგიკა
  const ev = new CustomEvent("open-cookie-settings");
  window.dispatchEvent(ev);

  // fallback შეტყობინება
  if (!window.__cookieSettingsHookShown) {
    window.__cookieSettingsHookShown = true;
    alert("Open cookie settings (wire this event to your CMP).");
  }
}

export default function CookiesPolicy() {
  const { language } = useLanguage();
  const isEn = language === "en";

  return (
    <Page>
      <Title>{isEn ? "Cookies Policy" : "ქუქიზების პოლიტიკა"}</Title>
      <Sub>
        {isEn
          ? "How we use cookies and similar technologies on shopaholics.ge"
          : "როგორ ვიყენებთ ქუქიზებს და მსგავს ტექნოლოგიებს shopaholics.ge-ზე"}
      </Sub>

      <MetaBar>
        <span><Small>{isEn ? "Last updated" : "ბოლოს განახლდა"}: {LAST_UPDATED}</Small></span>
        <span><Small>Shopaholics • shopaholics.ge</Small></span>
      </MetaBar>

      <Card>
        <H2>{isEn ? "At a glance" : "მოკლე მიმოხილვა"}</H2>
        <P>
          {isEn
            ? "We use cookies to keep our site working, remember your preferences, analyze traffic, and show relevant offers."
            : "ქუქიზებს ვიყენებთ საიტის გამართულად მუშაობისთვის, თქვენი პარამეტრების დასამახსოვრებლად, ტრაფიკის ანალიზისა და შესაბამისი შეთავაზებების საჩვენებლად."}
        </P>
        <TOC>
          <a href="#what">{isEn ? "What are cookies?" : "რა არის ქუქი?"}</a>
          <a href="#types">{isEn ? "Types of cookies we use" : "რა ტიპის ქუქიზებს ვიყენებთ"}</a>
          <a href="#why">{isEn ? "Why we use them" : "რისთვის ვიყენებთ"}</a>
          <a href="#third">{isEn ? "Third-party cookies" : "მესამე მხარის ქუქიზები"}</a>
          <a href="#control">{isEn ? "Your choices & control" : "თქვენი არჩევანი და კონტროლი"}</a>
          <a href="#changes">{isEn ? "Changes to this policy" : "პოლიტიკის ცვლილებები"}</a>
          <a href="#contact">{isEn ? "Contact us" : "კონტაქტი"}</a>
        </TOC>
      </Card>

      <Card id="what">
        <H2>{isEn ? "What are cookies?" : "რა არის ქუქი?"}</H2>
        <P>
          {isEn
            ? "Cookies are small text files placed on your device when you visit a website. They help the site remember information about your visit, so your experience is smoother and more useful next time."
            : "ქუქი მცირე ტექსტური ფაილია, რომელიც თქვენს მოწყობილობაზე ინახება ვებგვერდის მონახულებისას. ის ეხმარება საიტს, დაამახსოვროს თქვენი ვიზიტის ინფორმაცია, რათა შემდეგი გამოცდილება იყოს უფრო მოსახერხებელი და სასარგებლო."}
        </P>
        <P>
          {isEn
            ? "We also use similar technologies (like local storage and pixels) that work in a comparable way."
            : "ასევე ვიყენებთ მსგავს ტექნოლოგიებს (მაგ. Local Storage-სა და პიქსელებს), რომლებიც ანალოგიურად მუშაობს."}
        </P>
      </Card>

      <Card id="types">
        <H2>{isEn ? "Types of cookies we use" : "რა ტიპის ქუქიზებს ვიყენებთ"}</H2>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>{isEn ? "Category" : "კატეგორია"}</th>
                <th>{isEn ? "Purpose" : "დანიშნულება"}</th>
                <th>{isEn ? "Examples" : "მაგალითები"}</th>
                <th>{isEn ? "Duration" : "ვადა"}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>{isEn ? "Strictly necessary" : "სავალდებულო"}</b></td>
                <td>
                  {isEn
                    ? "Essential for the site to function (e.g., cart, checkout, security)."
                    : "აუცილებელია საიტის ფუნქციონირებისთვის (კალათა, გადახდა, უსაფრთხოება და სხვ.)."}
                </td>
                <td>
                  <code>session_id</code>, <code>cart_id</code>, <code>csrf_token</code>
                </td>
                <td>{isEn ? "Session / up to 1 year" : "სესია / 1 წლამდე"}</td>
              </tr>
              <tr>
                <td><b>{isEn ? "Preferences" : "პარამეტრები"}</b></td>
                <td>
                  {isEn
                    ? "Remember your language, region, and display choices."
                    : "იმახსოვრებს ენას, რეგიონსა და ვიზუალური ჩვენების არჩევანს."}
                </td>
                <td>
                  <code>lang</code>, <code>view_mode</code>
                </td>
                <td>{isEn ? "Up to 1 year" : "1 წლამდე"}</td>
              </tr>
              <tr>
                <td><b>{isEn ? "Analytics" : "ანალიტიკა"}</b></td>
                <td>
                  {isEn
                    ? "Understand how visitors use our store to improve performance and UX."
                    : "გვეხმარება გავიგოთ, როგორ იყენებენ ვიზიტორები მაღაზიას, რათა გავაუმჯობესოთ სიჩქარე და UX."}
                </td>
                <td>
                  <code>_ga</code>, <code>_ga_*</code>, <code>_gid</code> {/* GA4 examples */}
                </td>
                <td>{isEn ? "Up to 2 years" : "2 წლამდე"}</td>
              </tr>
              <tr>
                <td><b>{isEn ? "Marketing" : "მარკეტინგი"}</b></td>
                <td>
                  {isEn
                    ? "Show relevant offers and measure campaign performance."
                    : "გაჩვენებთ შესაბამის შეთავაზებებს და იზომება კამპანიების ეფექტიანობა."}
                </td>
                <td>
                  <code>_fbp</code>, <code>_gcl_au</code>
                </td>
                <td>{isEn ? "Up to 3 months (varies)" : "3 თვემდე (შეიძლება განსხვავდებოდეს)"}</td>
              </tr>
            </tbody>
          </Table>
        </TableWrap>
        <P>
          <Small>
            {isEn
              ? "Names and durations above are examples; actual cookies may vary over time."
              : "ზემოთ მოყვანილი სახელები და ვადები არის მაგალითები; რეალური ქუქიზები დროთა განმავლობაში შეიძლება შეიცვალოს."}
          </Small>
        </P>
      </Card>

      <Card id="why">
        <H2>{isEn ? "Why we use cookies" : "რისთვის ვიყენებთ"}</H2>
        <UL>
          <li>
            {isEn
              ? "To keep your session, cart, and checkout working reliably."
              : "სესიის, კალათისა და გადახდის სტაბილური მუშაობისთვის."}
          </li>
          <li>
            {isEn
              ? "To remember your preferences (language, region)."
              : "თქვენი პარამეტრების დასამახსოვრებლად (ენა, რეგიონი)."}
          </li>
          <li>
            {isEn
              ? "To measure performance and fix issues (analytics)."
              : "წარმადობის გასაზომად და ხარვეზების გამოსასწორებლად (ანალიტიკა)."}
          </li>
          <li>
            {isEn
              ? "To show relevant products and offers (marketing)."
              : "რელევანტური პროდუქტებისა და შეთავაზებების საჩვენებლად (მარკეტინგი)."}
          </li>
        </UL>
      </Card>

      <Card id="third">
        <H2>{isEn ? "Third-party cookies" : "მესამე მხარის ქუქიზები"}</H2>
        <P>
          {isEn
            ? "Some cookies are set by our trusted partners to provide analytics and advertising services. These partners may combine cookie data with other information you’ve provided to them or they’ve collected from your use of their services."
            : "ქვეთაგან ზოგი ქუქი იტვიფრება ჩვენს სანდო პარტნიორებთან ერთად ანალიტიკისა და რეკლამისთვის. პარტნიორებს შესაძლოა ქუქიზებში არსებული მონაცემები შეუთავსონ სხვა ინფორმაციას, რომელიც თავად მიაწოდეთ ან მათმა სერვისებმა მიიღეს."}
        </P>
        <UL>
          <li>
            {isEn
              ? "Analytics (e.g., Google Analytics)"
              : "ანალიტიკა (მაგ., Google Analytics)"}
          </li>
          <li>
            {isEn
              ? "Advertising/retargeting (e.g., Meta Pixel, Google Ads)"
              : "რეკლამა/რიტარგეთინგი (მაგ., Meta Pixel, Google Ads)"}
          </li>
          <li>
            {isEn
              ? "Payment and anti-fraud providers may set necessary cookies during checkout."
              : "გადახდის და თაღლითობის პრევენციის პროვაიდერებს გადახდისას შეიძლება დასჭირდეთ სავალდებულო ქუქიზები."}
          </li>
        </UL>
        <P>
          {isEn
            ? "Please review the privacy and cookie notices of those providers for details about how they handle data."
            : "გთხოვთ, გაეცნოთ ამ პროვაიდერების კონფიდენციალურობისა და ქუქიზების დოკუმენტებს დეტალებისთვის."}
        </P>
      </Card>

      <Card id="control">
        <H2>{isEn ? "Your choices & control" : "თქვენი არჩევანი და კონტროლი"}</H2>
        <P>
          {isEn
            ? "You can manage non-essential cookies any time. Strictly necessary cookies can’t be disabled because they are required for the site to work."
            : "არასავალდებულო ქუქიზების მართვა შეგიძლიათ ნებისმიერ დროს. სავალდებულო ქუქიზების გამორთვა შეუძლებელია, რადგან საიტის მუშაობისთვის აუცილებელია."}
        </P>

        <Banner>
          <P style={{margin:0}}>
            {isEn
              ? "Adjust your cookie preferences:"
              : "დაარეგულირეთ ქუქიზების პარამეტრები:"}
          </P>
          <ButtonRow>
            <BtnPrimary onClick={openCookieSettings}>
              {isEn ? "Manage cookies" : "ქუქიზების მართვა"}
            </BtnPrimary>
            <Btn onClick={() => { try { localStorage.setItem("ga_optout", "true"); alert(isEn ? "Analytics will be limited on this browser." : "ანალიტიკა შეიზღუდება ამ ბრაუზერში."); } catch {} }}>
              {isEn ? "Limit analytics" : "ანალიტიკის შეზღუდვა"}
            </Btn>
          </ButtonRow>
          <Small>
            {isEn
              ? "You can also clear or block cookies via your browser settings. Steps vary by browser and device."
              : "ასევე შეგიძლიათ ქუქიზების წაშლა/ბლოკირება ბრაუზერის პარამეტრებიდან. ინსტრუქცია განსხვავდება ბრაუზერისა და მოწყობილობის მიხედვით."}
          </Small>
        </Banner>
      </Card>

      <Card id="changes">
        <H2>{isEn ? "Changes to this policy" : "პოლიტიკის ცვლილებები"}</H2>
        <P>
          {isEn
            ? "We may update this Cookies Policy from time to time. We’ll post the updated version here and adjust the date above."
            : "ქუქიზების პოლიტიკა დროთა განმავლობაში შეიძლება განახლდეს. განახლებულ ვერსიას აქ გამოვაქვეყნებთ და ზემოთ მითითებულ თარიღსაც განვაახლებთ."}
        </P>
      </Card>

      <Card id="contact">
        <H2>{isEn ? "Contact us" : "კონტაქტი"}</H2>
        <P>
          {isEn
            ? <>If you have any questions about cookies on <b>shopaholics.ge</b>, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</>
            : <>თუ გაქვთ კითხვები ქუქიზებთან დაკავშირებით <b>shopaholics.ge</b>-ზე, მოგვწერეთ: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</>}
        </P>
      </Card>
    </Page>
  );
}
